import { useState, useRef, useEffect, useCallback } from 'react';

interface UseRowScrollProps {
  containerWidth: number;
  slotsLength: number;
}

interface UseRowScrollReturn {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  updateScrollability: () => void;
}

const useRowScroll = ({ 
  containerWidth, 
  slotsLength 
}: UseRowScrollProps): UseRowScrollReturn => {
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const updateScrollability = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      // Add a small tolerance (e.g., 1px) for floating point inaccuracies
      // WEB-SPECIFIC: More robust scrolling detection similar to RN implementation
      setCanScrollLeft(scrollLeft > 5); // Match RN's 5px threshold
      setCanScrollRight(scrollWidth - scrollLeft - clientWidth > 5); // Match RN's 5px threshold
    }
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    updateScrollability(); // Initial check

    const handleResize = () => updateScrollability();
    window.addEventListener('resize', handleResize);
    
    if (container) {
      // Also update on any content changes that might affect scrollWidth
      const observer = new MutationObserver(updateScrollability);
      observer.observe(container, { childList: true, subtree: true });
      
      return () => {
        window.removeEventListener('resize', handleResize);
        observer.disconnect();
      };
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateScrollability, slotsLength]); // Re-run if slots change, as this affects scrollWidth

  return {
    scrollContainerRef,
    canScrollLeft,
    canScrollRight,
    updateScrollability,
  };
};

export default useRowScroll;
